import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Brand from '../common/Brand';

interface ErrorPageAction {
  label: string;
  to?: string;
  onClick?: () => void;
}

interface ErrorPageProps {
  statusCode: number;
  title: string;
  message: string;
  requestId?: string | null;
  icon?: ReactNode;
  primaryAction: ErrorPageAction;
  secondaryAction?: ErrorPageAction;
  auxiliaryAction?: ErrorPageAction;
}

function ActionButton({
  action,
  variant = 'primary'
}: {
  action: ErrorPageAction;
  variant?: 'primary' | 'secondary' | 'auxiliary';
}) {
  const classesByVariant: Record<'primary' | 'secondary' | 'auxiliary', string> = {
    primary:
      'inline-flex items-center justify-center rounded-xl bg-primary-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
    secondary:
      'inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus-visible:ring-offset-slate-900',
    auxiliary:
      'inline-flex items-center justify-center rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-800 transition hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:border-primary-900/40 dark:bg-primary-900/20 dark:text-primary-200 dark:hover:bg-primary-900/30 dark:focus-visible:ring-offset-slate-900'
  };

  // Use plain anchors so this page also works when rendered outside <Router>
  // (e.g. AppErrorBoundary wraps the app above BrowserRouter in main.tsx).
  if (action.to) {
    return (
      <a href={action.to} className={classesByVariant[variant]}>
        {action.label}
      </a>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={classesByVariant[variant]}>
      {action.label}
    </button>
  );
}

export default function ErrorPage({
  statusCode,
  title,
  message,
  requestId,
  icon,
  primaryAction,
  secondaryAction,
  auxiliaryAction
}: ErrorPageProps) {
  const { t } = useTranslation('errors');
  const rawErrorCodeLabel = t('errorCode', { statusCode, defaultValue: 'Error {{statusCode}}' });
  const errorCodeLabel = rawErrorCodeLabel === 'errorCode'
    ? `Error ${statusCode}`
    : String(rawErrorCodeLabel).replace('{{statusCode}}', String(statusCode));
  const rawRequestIdLabel = t('requestIdLabel', { defaultValue: 'Support request ID' });
  const requestIdLabel = rawRequestIdLabel === 'requestIdLabel' ? 'Support request ID' : rawRequestIdLabel;

  return (
    <div className="error-experience app-shell flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
      <section
        aria-labelledby="error-page-title"
        className="panel-surface w-full max-w-2xl rounded-3xl border border-slate-200/70 p-6 sm:p-8 dark:border-slate-700"
      >
        <div className="mb-6"><Brand /></div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-700 dark:text-primary-300">
          {errorCodeLabel}
        </p>
        <div className="mt-3 flex items-start gap-3">
          {icon && <div className="mt-0.5 text-primary-700 dark:text-primary-300">{icon}</div>}
          <div className="min-w-0">
            <h1 id="error-page-title" className="section-heading text-3xl text-slate-900 dark:text-slate-100 sm:text-4xl">
              {title}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300 sm:text-base">{message}</p>
          </div>
        </div>

        {requestId && (
          <p className="mt-5 break-words rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
            {requestIdLabel}: <span className="font-semibold text-slate-800 dark:text-slate-100">{requestId}</span>
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <ActionButton action={primaryAction} variant="primary" />
          {secondaryAction && <ActionButton action={secondaryAction} variant="secondary" />}
          {auxiliaryAction && <ActionButton action={auxiliaryAction} variant="auxiliary" />}
        </div>
      </section>
    </div>
  );
}
